import { createFileRoute, Link } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ACTIVITY_TYPES } from '@/admin/people/activityTypes';
import { activityFilterQuery } from '@/admin/people/peopleFilters';
import { tr, usePeopleT } from '@/admin/people/strings';
import { Badge } from '@/admin/ui/Badge';
import { createTablePage } from '@/admin/ui/createTablePage';
import {
  readTableSearch,
  type TableFilters,
  writeTableSearch,
} from '@/admin/ui/tableSearch';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import { renderActivityParts } from '~/modules/activities/renderActivityParts';
import type { FilterField } from '~/styles/tables/filterandSettingsStyles';

type AdminActivityItem = components['schemas']['AdminActivityItem'];

// Legacy's admin datatables page twenty at a time.
const PAGE_SIZE = 20;
const EMPTY = '—';

type ActivitySearch = {
  page?: number;
  q?: string;
  sort?: string;
  order?: 'asc' | 'desc';
  filters?: TableFilters;
};

const columnHelper = createColumnHelper<AdminActivityItem>();

function UserCell({ item }: { item: AdminActivityItem }) {
  if (!item.user) return <>{EMPTY}</>;
  return (
    <Link to="/admin/people/$userId/" params={{ userId: String(item.user.id) }}>
      {item.user.display_name}
    </Link>
  );
}

function ActivityCell({ item }: { item: AdminActivityItem }) {
  const { t } = useTranslation();
  return (
    <>
      <Sentence>{renderActivityParts(item.description, t, item.id)}</Sentence>
      {item.body && <Body>{item.body}</Body>}
    </>
  );
}

function HiddenBadge({ item }: { item: AdminActivityItem }) {
  const t = usePeopleT();
  return item.hidden ? (
    <Badge $variant="neutral">{t('admin.activity.badge.hidden')}</Badge>
  ) : null;
}

const columns = [
  columnHelper.display({
    id: 'user',
    header: () => tr('admin.activity.col.user'),
    cell: ({ row }) => <UserCell item={row.original} />,
  }),
  columnHelper.display({
    id: 'activity',
    header: () => tr('admin.activity.col.activity'),
    cell: ({ row }) => (
      <>
        <ActivityCell item={row.original} />
        <HiddenBadge item={row.original} />
      </>
    ),
  }),
  // The type key, not BuddyPress's registered label.
  columnHelper.accessor('type', {
    header: () => tr('admin.activity.col.action'),
  }),
  columnHelper.accessor((row) => new Date(row.created_at).toLocaleString(), {
    id: 'created_at',
    header: () => tr('admin.activity.col.posted'),
  }),
];

function useActivityFilterFields(): FilterField[] {
  return [
    {
      key: 'type',
      label: tr('admin.activity.filters.type'),
      type: 'multiselect',
      options: ACTIVITY_TYPES.map((value) => ({ value, label: value })),
    },
    {
      key: 'posted_from',
      label: tr('admin.activity.filters.postedFrom'),
      type: 'date',
      groupWith: 'posted_to',
    },
    {
      key: 'posted_to',
      label: tr('admin.activity.filters.postedTo'),
      type: 'date',
    },
    {
      key: 'user_id',
      label: tr('admin.activity.filters.userId'),
      type: 'text',
    },
  ];
}

// No row link: the user cell links to the person, and a row without a user has
// nowhere to go.
const ActivityLog = createTablePage<AdminActivityItem, '/admin/people/$userId'>(
  {
    name: 'admin.nav.activity',
    pageSize: PAGE_SIZE,
    urlState: true,
    // The API orders newest first and offers no other order.
    useData: (_page, _search, { paging, filters }) =>
      api.listAdminActivity.useQuery({
        query: {
          limit: paging.limit,
          offset: paging.offset,
          ...activityFilterQuery(filters, ACTIVITY_TYPES),
        },
      }),
    filterFields: useActivityFilterFields,
    columns,
  },
);

export const Route = createFileRoute('/admin/activity')({
  staticData: { minTier: 'FRONT_END_SUPPORT' },
  validateSearch: (raw: Record<string, unknown>): ActivitySearch =>
    writeTableSearch({}, readTableSearch(raw)) as ActivitySearch,
  component: ActivityLog,
});

const Sentence = styled.div`
  font-size: 0.875rem;
`;

const Body = styled.div`
  color: var(--grey-600);
  font-size: 0.8125rem;
  margin-top: 0.25rem;
`;
